import type { BaseApiClient } from './BaseApiClient';
import { baseApiClient } from './BaseApiClient';
import type {
  BillingPlansResponse,
  BillingProfileResponse,
  CancelSubscriptionRequest,
  ChangePlanRequest,
  ChangePlanResponse,
  KycDetailsRequest,
  OrbCreditLedgerParams,
  OrbCreditLedgerResponse,
  OrbInvoiceParams,
  OrbInvoiceResponse,
  OrbSubscriptionResponse,
  OrbUsageParams,
  OrbUsageResponse,
  SetupIntentResponse,
  SubscriptionDetailResponse,
  UpdateBillingProfileRequest,
} from './types';

class BillingClient {
  constructor(private apiClient: BaseApiClient) {}

  getSubscriptions = () => {
    return this.apiClient.get<OrbSubscriptionResponse>('/p/v1/billing/subscriptions/');
  };

  getInvoices = (params: OrbInvoiceParams) => {
    return this.apiClient.get<OrbInvoiceResponse>('/p/v1/billing/invoices/', params);
  };

  getUsage = (params: OrbUsageParams) => {
    return this.apiClient.get<OrbUsageResponse>('/p/v1/billing/usage/', params);
  };

  downloadUsageCSV = (params: OrbUsageParams) => {
    return this.apiClient.download<Blob>(
      '/p/v1/billing/usage/',
      {
        ...params,
        format: 'csv',
      },
      'GET',
      'text/csv',
    );
  };

  getCreditLedger = (params: OrbCreditLedgerParams) => {
    return this.apiClient.get<OrbCreditLedgerResponse>('/p/v1/billing/credits/ledger/', params);
  };

  getBillingProfile = () => {
    return this.apiClient.get<BillingProfileResponse>('/p/v1/billing/profile/');
  };

  // Goes through dashboard_backend's BillingProfileView (not the proxy) so the
  // company_type flip + Auth0 sync side effects fire on subscription creation.
  updateBillingProfile = (payload: UpdateBillingProfileRequest) => {
    return this.apiClient.patch<BillingProfileResponse>('/v1/billing/profile/', payload);
  };

  setupIntent = () => {
    return this.apiClient.post<SetupIntentResponse>('/p/v1/billing/intents/', {});
  };

  getPlans = () => {
    return this.apiClient.get<BillingPlansResponse>('/p/v1/billing/plans/');
  };

  getSubscription = () => {
    return this.apiClient.get<SubscriptionDetailResponse>('/p/v1/billing/subscription/');
  };

  cancelSubscription = (payload: CancelSubscriptionRequest = {}) => {
    return this.apiClient.post<SubscriptionDetailResponse>('/p/v1/billing/subscription/cancel/', payload);
  };

  reactivateSubscription = () => {
    return this.apiClient.post<SubscriptionDetailResponse>('/p/v1/billing/subscription/reactivate/', {});
  };

  // change-plan uses dashboard_backend's dedicated view (not the generic proxy)
  // because it triggers a Company.company_type flip on conversion.
  changePlan = (payload: ChangePlanRequest) => {
    return this.apiClient.post<ChangePlanResponse>('/v1/billing/subscription/change-plan/', payload);
  };

  submitKycDetails = (payload: KycDetailsRequest) => {
    return this.apiClient.post<void>('/v1/kyc/', payload);
  };
}

export const billingClient = new BillingClient(baseApiClient);
